import { RouterState } from 'connected-react-router';
import { RecordOf } from 'immutable';
import { get } from 'lodash';
import { Action, ReducersMapObject } from 'redux';
import { createSelector } from 'reselect';

import { Paths } from '../types/common';
import { IChatMessagesState } from './chat-messages/types';
import { IFileState } from './file/types';
import { INotification, NotificationUI } from './notification/types';
import { ISearchState } from './search/types';
import { ISelectedAppointmentContactState } from './selected-contact/types';
import { UserState } from './user/types';

export interface StandardAction<T = any> {
  type: string;
  payload?: T;
  error?: string;
  meta?: unknown;
}

export interface IAsyncState<T> {
  isFetching: boolean;
  isUpdating: boolean;
  data: T;
}

export type CurrentUserType = RecordOf<IAsyncState<UserState>>;
export type INotificationState = IAsyncState<NormalizedEndpoint<INotification>> & NotificationUI;

export interface RootState {
  readonly router: RouterState;
  readonly settings: {
    navStyle: string;
    layoutType: string;
    themeType: string;
    themeColor: string;
    isDirectionRTL: boolean;
    locale: {
      languageId: string;
      locale: 'en';
      name: string;
      icon: string;
    };
  };
  readonly common: any;
  readonly auth: any;
  readonly user: CurrentUserType;
  readonly file: RecordOf<IFileState>;
  readonly notification: INotificationState;
  readonly search: RecordOf<ISearchState>;
  readonly selectedAppointmentContact: RecordOf<ISelectedAppointmentContactState>;
  readonly chatMessages: RecordOf<IChatMessagesState>;
}

export type Reducers = ReducersMapObject<RootState>;

export type LoadedState = Partial<RootState>;

export type LoadedReducers = Partial<Reducers>;

export type MetaAction<Type, Meta, Error> = Action<Type> & {
  meta?: Meta;
  error?: Error;
};

export type PayloadAction<Type, Payload = void, Meta = void, Error = void> = MetaAction<Type, Meta, Error> & {
  readonly payload?: Payload;
};

/**
 * @see https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape
 */
export type NormalizedEndpoint<D = any, ID extends string | number = string> = {
  allIds: ID[];
  byId: Record<ID, D>;
};

/**
 * Generic selector for redux normalized endpoint
 */
export class EndpointSelector<D = unknown, ID extends string | number = string> {
  /**
   * @see Paths
   * @param path path of root state
   */
  constructor(private path: Paths<RootState>) {}

  /**
   * Select the endpoint
   */
  select = (state: RootState): NormalizedEndpoint<D, ID> => get(state, this.path);

  /**
   * Get `allIds` array
   */
  getAllIds = (state: RootState): ID[] => this.select(state).allIds;

  /**
   * Get `byId` object
   */
  getById = (state: RootState) => this.select(state).byId;

  /**
   * Select one by Id
   */
  getOneById = (state: RootState) => (id: ID): D | undefined => this.select(state).byId[id];

  /**
   * Get merged data
   */
  getAll = createSelector([this.getAllIds, this.getById], (allIds, byId) =>
    allIds.reduce((items: D[], id: ID) => {
      if (byId[id] !== undefined) {
        items.push(byId[id] as D);
      }

      return items;
    }, []),
  );
}
